import { useState, useEffect } from 'react';
import { categoriesApi, Category } from '../../api/categories.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';

const CategoryList = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [modalType, setModalType] = useState<'parent' | 'sub'>('parent');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategory: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const data = await categoriesApi.getAll();
      setCategories(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kategoriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (type: 'parent' | 'sub', category?: Category) => {
    setModalType(type);
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        parentCategory: category.parentCategory?._id || '',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        parentCategory: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        parentCategory: modalType === 'sub' ? formData.parentCategory : undefined,
      };

      if (editingCategory) {
        await categoriesApi.update(editingCategory.id, submitData);
      } else {
        await categoriesApi.create(submitData);
      }
      await fetchCategories();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await categoriesApi.delete(id);
      await fetchCategories();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  // Separate parent and sub categories
  const parentCategories = categories.filter((cat) => !cat.parentCategory);
  const subCategories = categories.filter((cat) => cat.parentCategory);

  // Get available parent categories for selection (exclude current category when editing)
  const availableParentCategories = parentCategories.filter(
    (cat) => !editingCategory || cat.id !== editingCategory.id
  );

  const parentColumns: Column<Category>[] = [
    { 
      key: 'name', 
      header: 'Kategori Adı',
      render: (item) => (
        <span className="font-semibold text-gray-900">{item.name}</span>
      )
    },
    {
      key: 'subCount',
      header: 'Alt Sayısı',
      render: (item) => {
        const count = subCategories.filter(sub => sub.parentCategory?._id === item.id).length;
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {count}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal('parent', item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  const subColumns: Column<Category>[] = [
    { 
      key: 'name', 
      header: 'Kategori Adı',
      render: (item) => (
        <span className="font-medium text-gray-900">{item.name}</span>
      )
    },
    {
      key: 'parentCategory',
      header: 'Üst Kategori',
      render: (item) => (
        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-md text-sm font-medium">
          {item.parentCategory?.name || '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal('sub', item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Kategoriler yükleniyor..." />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Kategori Yönetimi</h1>
        <p className="text-gray-600 text-sm">
          Üst kategoriler ana grupları, alt kategoriler ise detaylı sınıflandırmaları temsil eder.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Parent Categories Section */}
        <div className="flex flex-col">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">📁 Üst Kategoriler</h2>
              <Button size="sm" onClick={() => handleOpenModal('parent')}>
                + Ekle
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Ana kategori grupları (Örn: Elektronik, Mobilya)
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
            <Table 
              data={parentCategories} 
              columns={parentColumns} 
              emptyMessage="Henüz üst kategori yok" 
            />
          </div>
        </div>

        {/* Sub Categories Section */}
        <div className="flex flex-col">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">📂 Alt Kategoriler</h2>
              <Button 
                size="sm"
                onClick={() => handleOpenModal('sub')}
                disabled={parentCategories.length === 0}
              >
                + Ekle
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Üst kategorilere bağlı detaylar (Örn: Bilgisayar, Telefon)
            </p>
          </div>
          {parentCategories.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <svg className="w-10 h-10 text-yellow-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-yellow-800 font-medium text-sm">Önce üst kategori ekleyin</p>
              <p className="text-yellow-600 text-xs mt-1">Sol taraftan başlayın</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
              <Table 
                data={subCategories} 
                columns={subColumns} 
                emptyMessage="Henüz alt kategori yok" 
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          editingCategory 
            ? `${modalType === 'parent' ? 'Üst' : 'Alt'} Kategori Düzenle` 
            : `Yeni ${modalType === 'parent' ? 'Üst' : 'Alt'} Kategori Ekle`
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              {modalType === 'parent' 
                ? '📁 Üst kategori ekliyorsunuz. Bu ana bir grup olacak (Örn: Elektronik, Mobilya)' 
                : '📂 Alt kategori ekliyorsunuz. Bu bir üst kategoriye bağlı olacak (Örn: Bilgisayar, Telefon)'}
            </p>
          </div>
          
          <Input
            label={`${modalType === 'parent' ? 'Üst' : 'Alt'} Kategori Adı`}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={modalType === 'parent' ? 'Örn: Elektronik' : 'Örn: Bilgisayar'}
            required
          />
          
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Kategori hakkında kısa açıklama (opsiyonel)"
          />
          
          {modalType === 'sub' && (
            <Select
              label="Bağlı Olduğu Üst Kategori"
              value={formData.parentCategory}
              onChange={(value) => setFormData({ ...formData, parentCategory: value })}
              options={[
                { value: '', label: 'Üst kategori seçiniz' },
                ...availableParentCategories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
              required
            />
          )}
          
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingCategory ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CategoryList;
